import { useState, useEffect } from 'react';
import { Outlet, useNavigate, NavLink } from 'react-router-dom';
import { 
  Home, Calendar, DollarSign, Star, BookOpen, User, LogOut, Menu, X, 
  FileText, MessageSquare
} from 'lucide-react';
import { useContractorAuth } from '../../contexts/ContractorAuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_jax-craft-remodel/artifacts/6r3h6sd1_Transparent%20Logo.png";

export const ContractorPortalLayout = () => {
  const navigate = useNavigate();
  const { contractor, logout, loading, isAuthenticated, agreementSigned } = useContractorAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/portal/login');
    } else if (!loading && isAuthenticated && !agreementSigned) {
      navigate('/contractor/agreement');
    }
  }, [loading, isAuthenticated, agreementSigned, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const navItems = [
    { to: '/contractor', icon: Home, label: 'Dashboard', end: true },
    { to: '/contractor/schedule', icon: Calendar, label: 'Schedule' },
    { to: '/contractor/pay', icon: DollarSign, label: 'Pay' },
    { to: '/contractor/performance', icon: Star, label: 'Performance' },
    { to: '/contractor/training', icon: BookOpen, label: 'Training' },
    { to: '/contractor/documents', icon: FileText, label: 'My Documents' },
    { to: '/contractor/messages', icon: MessageSquare, label: 'Messages' },
    { to: '/contractor/profile', icon: User, label: 'Profile' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !agreementSigned) {
    return null; // Will redirect
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Top Navigation Bar */}
      <header className="bg-[#1a1a1a] border-b border-white/10 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 h-16">
          {/* Logo and Menu Button */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-white p-2"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <img src={LOGO_URL} alt="Jax Remodeling" className="h-8 w-auto" />
          </div>

          {/* User Info */}
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <p className="text-white text-sm font-medium">{contractor?.name}</p>
              <p className="text-[#C6A87C] text-xs font-mono">{contractor?.contractor_id}</p>
            </div>
            <div className="w-10 h-10 rounded-full bg-[#C6A87C]/20 flex items-center justify-center overflow-hidden">
              {contractor?.profile_picture_url ? (
                <img 
                  src={contractor.profile_picture_url} 
                  alt={contractor?.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-5 h-5 text-[#C6A87C]" />
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-[#1a1a1a] border-r border-white/10 transform transition-transform duration-200 lg:transform-none ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          } mt-16 lg:mt-0`}
        >
          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                    isActive
                      ? 'bg-[#C6A87C]/20 text-[#C6A87C] border-l-2 border-[#C6A87C]'
                      : 'text-gray-400 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </NavLink>
            ))}

            <div className="pt-4 mt-4 border-t border-white/10">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 text-sm text-gray-400 hover:text-red-400 hover:bg-red-400/5 w-full transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </nav>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-6 min-h-[calc(100vh-4rem)] pb-20 lg:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Bottom Navigation for Mobile */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#1a1a1a] border-t border-white/10 z-40">
        <div className="flex justify-around py-2">
          {navItems.slice(0, 5).map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center py-2 px-3 ${
                  isActive ? 'text-[#C6A87C]' : 'text-gray-500'
                }`
              }
            >
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] mt-1">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default ContractorPortalLayout;
